import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser, getOperator,
  ViewIcon, EditIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// NOT: /api/b2b-returns OData değil — REST (MediatR-stili) bir uç. Tüm kayıtlar (max 200)
// tek istekte gelir; filtre / sıralama / sayfalama istemci tarafında yapılır. useC360List
// (OData $top/$skip/$filter beklediği için) kullanılmaz; c360 görsel kabuğu korunur.
const API_BASE = '/api/b2b-returns';
const PAGE_SIZE_DEFAULT = 20;

const STATUS_META = {
  0: { label: 'Beklemede', tone: 'wait' },
  1: { label: 'Siparişe Çevrildi', tone: 'ok' },
  2: { label: 'Yanlış İade', tone: 'bad' },
};
const STATUS_OPTIONS = [
  { value: '0', label: 'Beklemede' },
  { value: '1', label: 'Siparişe Çevrildi' },
  { value: '2', label: 'Yanlış İade' },
];

// c360 kolon filtre metası — istemci tarafı değerlendirme için odataField yerine
// satırdan değer çıkaran getter kullanılır; filterModel'in operatör seti aynen geçerli.
const COLUMN_FILTER_META = {
  returnNumber: { type: 'text', odataField: 'ReturnNumber' },
  pointName: { type: 'text', odataField: 'PointName' },
  amount: { type: 'number', odataField: 'Amount' },
  itemCount: { type: 'number', odataField: 'ItemCount' },
  source: { type: 'text', odataField: 'Source' },
  status: { type: 'select', odataField: 'Status', valueFormat: 'raw', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['returnNumber', 'pointName', 'pointCode', 'source'];

const STATUS_TABS = [
  { key: 'all', label: 'Tümü' },
  { key: 0, label: 'Beklemede' },
  { key: 1, label: 'Çevrilen' },
  { key: 2, label: 'Reddedilen' },
];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  const items = pick(data, 'items', 'Items') ?? [];
  const pendingCount = pick(data, 'pendingCount', 'PendingCount') ?? 0;
  return { items: Array.isArray(items) ? items : [], pendingCount };
}

function statusMeta(status) {
  return STATUS_META[status] || { label: String(status ?? '-'), tone: 'neutral' };
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDateShort(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR');
}

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
}

// === İstemci tarafı filtre değerlendirme (c360 filterModel operatörlerine uyumlu) ===
function evalTextFilter(raw, filter) {
  const op = getOperator('text', filter.op);
  const cell = String(raw ?? '');
  if (op.id === 'blank') return cell.trim() === '';
  if (op.id === 'notBlank') return cell.trim() !== '';
  const v = String(filter.value ?? '');
  if (op.needsValue && v === '') return true;
  const c = cell.toLocaleLowerCase('tr-TR');
  const q = v.toLocaleLowerCase('tr-TR');
  switch (op.id) {
    case 'contains': return c.includes(q);
    case 'notContains': return !c.includes(q);
    case 'equals': return c === q;
    case 'notEquals': return c !== q;
    case 'startsWith': return c.startsWith(q);
    case 'endsWith': return c.endsWith(q);
    default: return true;
  }
}

function evalNumberFilter(raw, filter) {
  const op = getOperator('number', filter.op);
  const hasVal = raw !== null && raw !== undefined && raw !== '';
  if (op.id === 'blank') return !hasVal;
  if (op.id === 'notBlank') return hasVal;
  const n = Number(raw);
  const v = Number(filter.value);
  const v2 = Number(filter.value2);
  if (op.needsValue && (filter.value === '' || filter.value == null)) return true;
  if (!Number.isFinite(n)) return false;
  switch (op.id) {
    case 'equals': return n === v;
    case 'notEquals': return n !== v;
    case 'gt': return n > v;
    case 'gte': return n >= v;
    case 'lt': return n < v;
    case 'lte': return n <= v;
    case 'between': return n >= v && n <= v2;
    default: return true;
  }
}

function evalSelectFilter(raw, filter) {
  if (filter.value === '' || filter.value == null) return true;
  return String(raw) === String(filter.value);
}

function rowFilterValue(columnId, row) {
  switch (columnId) {
    case 'returnNumber': return pick(row, 'returnNumber', 'ReturnNumber');
    case 'pointName': return pick(row, 'pointName', 'PointName');
    case 'amount': return pick(row, 'amount', 'Amount');
    case 'itemCount': return pick(row, 'itemCount', 'ItemCount');
    case 'source': return pick(row, 'source', 'Source');
    case 'status': return pick(row, 'status', 'Status');
    default: return undefined;
  }
}

function matchesColumnFilters(row, columnFilters) {
  for (const [columnId, filter] of Object.entries(columnFilters)) {
    if (!filter) continue;
    const meta = COLUMN_FILTER_META[columnId];
    if (!meta) continue;
    const raw = rowFilterValue(columnId, row);
    let ok = true;
    if (meta.type === 'text') ok = evalTextFilter(raw, filter);
    else if (meta.type === 'number') ok = evalNumberFilter(raw, filter);
    else if (meta.type === 'select') ok = evalSelectFilter(raw, filter);
    if (!ok) return false;
  }
  return true;
}

export function B2bReturnsPage() {
  usePageMeta('B2B İadeler', 'B2B sistemi ve OT portalından gelen iade kayıtları');
  const { canEdit, canDelete } = usePageActions();

  const [allRows, setAllRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [statusTab, setStatusTab] = useState('all');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const [sortField, setSortField] = useState('ReceivedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const { items, pendingCount: pc } = unwrapResponse(payload);
      setAllRows(items);
      setPendingCount(pc);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  }, []);
  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  }, []);

  // İstemci tarafı: tab + serbest metin + kolon filtreleri + sıralama
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (statusTab !== 'all' && pick(row, 'status', 'Status') !== statusTab) return false;
      if (!matchesColumnFilters(row, columnFilters)) return false;
      if (!needle) return true;
      return SEARCH_FIELDS.some((f) => {
        const v = rowFilterValue(f, row) ?? row[f];
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    };
    const filtered = allRows.filter(match);

    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'PointName': return pick(row, 'pointName', 'PointName') || '';
        case 'ReturnNumber': return pick(row, 'returnNumber', 'ReturnNumber') || '';
        case 'Amount': return Number(pick(row, 'amount', 'Amount')) || 0;
        case 'ItemCount': return Number(pick(row, 'itemCount', 'ItemCount')) || 0;
        case 'Status': return Number(pick(row, 'status', 'Status')) || 0;
        case 'Source': return pick(row, 'source', 'Source') || '';
        case 'ReceivedAt':
        default: {
          const v = pick(row, 'receivedAt', 'ReceivedAt');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a); const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, statusTab, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const performAction = useCallback(async (row, action, successMessage) => {
    const id = pick(row, 'id', 'Id');
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/${action}`, { method: 'POST', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      showToast('success', successMessage);
      await fetchData();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setActionBusyId(null);
    }
  }, [showToast, fetchData]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor((row) => pick(row, 'returnNumber', 'ReturnNumber'), {
      id: 'returnNumber', header: 'İade No', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor((row) => pick(row, 'pointName', 'PointName'), {
      id: 'pointName', header: 'Nokta', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const name = pick(row.original, 'pointName', 'PointName') || '-';
        const code = pick(row.original, 'pointCode', 'PointCode');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 700 }}>{name}</span>
            {code && <span style={{ fontSize: 12, color: '#6b7280' }}>{code}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => pick(row, 'amount', 'Amount'), {
      id: 'amount', header: 'Tutar', meta: { filterType: 'number' },
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor((row) => pick(row, 'itemCount', 'ItemCount'), {
      id: 'itemCount', header: 'Kalem', meta: { filterType: 'number' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined) ? '-' : v; },
    }),
    columnHelper.accessor((row) => pick(row, 'source', 'Source'), {
      id: 'source', header: 'Kanal', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor((row) => pick(row, 'status', 'Status'), {
      id: 'status', header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => { const m = statusMeta(info.getValue()); return <StatusBadge tone={m.tone}>{m.label}</StatusBadge>; },
    }),
    columnHelper.accessor((row) => pick(row, 'receivedAt', 'ReceivedAt'), {
      id: 'receivedAt', header: 'Geliş Tarihi', enableColumnFilter: false,
      cell: (info) => formatDateShort(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => {
        const r = row.original;
        const id = pick(r, 'id', 'Id');
        const status = pick(r, 'status', 'Status');
        const busy = actionBusyId === id;
        return (
          <span className="c360-actions" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={() => setDetailRow(r)}><ViewIcon /></button>
            {status === 0 && canEdit && (
              <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Siparişe çevrildi olarak işaretle" aria-label="Çevrildi" disabled={busy} onClick={() => performAction(r, 'convert', 'Siparişe çevrildi olarak işaretlendi')}><EditIcon /></button>
            )}
            {status === 0 && canDelete && (
              <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Yanlış iade olarak işaretle" aria-label="Yanlış" disabled={busy} onClick={() => performAction(r, 'dismiss', 'Yanlış iade olarak işaretlendi')}>✕</button>
            )}
          </span>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [actionBusyId, performAction, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { setSortField('ReceivedAt'); setSortDir('desc'); setCurrentPage(1); return; }
    setSortField(toODataSortField(first.id));
    setSortDir(first.desc ? 'desc' : 'asc');
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'İade No', valueGetter: ({ row }) => pick(row, 'returnNumber', 'ReturnNumber') || '' },
        { headerName: 'Nokta', valueGetter: ({ row }) => pick(row, 'pointName', 'PointName') || '' },
        { headerName: 'Nokta Kodu', valueGetter: ({ row }) => pick(row, 'pointCode', 'PointCode') || '' },
        { headerName: 'Tutar', valueGetter: ({ row }) => Number(pick(row, 'amount', 'Amount')) || 0 },
        { headerName: 'Kalem', valueGetter: ({ row }) => pick(row, 'itemCount', 'ItemCount') ?? '' },
        { headerName: 'Kanal', valueGetter: ({ row }) => pick(row, 'source', 'Source') || '' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusMeta(pick(row, 'status', 'Status')).label },
        { headerName: 'Geliş Tarihi', valueGetter: ({ row }) => formatDate(pick(row, 'receivedAt', 'ReceivedAt')) },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `b2b-iadeler_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <span style={{ display: 'inline-flex', gap: 6, marginRight: 4 }}>
        {STATUS_TABS.map((tab) => {
          const active = statusTab === tab.key;
          return (
            <button
              key={String(tab.key)}
              type="button"
              onClick={() => { setStatusTab(tab.key); setCurrentPage(1); }}
              style={{ ...(active ? s.btnPrimary : s.btnSecondary), padding: '6px 14px', fontSize: 13 }}
            >
              {tab.label}
            </button>
          );
        })}
      </span>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Nokta / iade no / kanal..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="B2B İadeler"
        subtitle="B2B sistemi ve OT portalından gelen iade kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 999,
            background: 'rgba(253, 171, 61, 0.14)',
            border: '1px solid rgba(253, 171, 61, 0.32)',
            color: '#92400e', fontWeight: 800, fontSize: 13,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--warn)' }} />
            {pendingCount} bekleyen
          </span>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => setDetailRow(row)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{pick(detailRow, 'returnNumber', 'ReturnNumber') || 'İade Detayı'}</h3>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Nokta</label>
                <div style={s.inputView}>
                  {pick(detailRow, 'pointName', 'PointName') || '-'}
                  {pick(detailRow, 'pointCode', 'PointCode') && (
                    <span style={{ color: '#6b7280', marginLeft: 8 }}>
                      ({pick(detailRow, 'pointCode', 'PointCode')})
                    </span>
                  )}
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Tutar</label>
                  <div style={s.inputView}>{formatCurrency(pick(detailRow, 'amount', 'Amount'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Kalem Sayısı</label>
                  <div style={s.inputView}>{pick(detailRow, 'itemCount', 'ItemCount') ?? '-'}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kanal</label>
                  <div style={s.inputView}>{pick(detailRow, 'source', 'Source') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>
                    {(() => { const m = statusMeta(pick(detailRow, 'status', 'Status')); return <StatusBadge tone={m.tone}>{m.label}</StatusBadge>; })()}
                  </div>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Geliş Tarihi</label>
                <div style={s.inputView}>{formatDate(pick(detailRow, 'receivedAt', 'ReceivedAt'))}</div>
              </div>
            </div>
            <div style={s.modalFooter}>
              {pick(detailRow, 'status', 'Status') === 0 && canDelete && (
                <button
                  type="button"
                  style={s.btnDanger}
                  onClick={() => { const r = detailRow; setDetailRow(null); performAction(r, 'dismiss', 'Yanlış iade olarak işaretlendi'); }}
                >
                  Yanlış İade
                </button>
              )}
              {pick(detailRow, 'status', 'Status') === 0 && canEdit && (
                <button
                  type="button"
                  style={{ ...s.btnPrimary, background: '#16a34a' }}
                  onClick={() => { const r = detailRow; setDetailRow(null); performAction(r, 'convert', 'Siparişe çevrildi olarak işaretlendi'); }}
                >
                  Siparişe Çevrildi
                </button>
              )}
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default B2bReturnsPage;
